/**
 * Visual answers under an Elio message: destination cards, a comparison, places and a route.
 * Display only. Buttons continue the conversation (they send a message to Elio); nothing here
 * changes the trip. Maps are drawn in the app; navigation links open Google Maps.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { ArrowRight, Bike, Bus, CableCar, CalendarDays, Car, Check, TramFront, Clock, ExternalLink, Footprints, MapPin, Minus, Navigation, Plane, Ship, Sparkles, Star, TrainFront, Wallet } from 'lucide-react';
import type { Block, ComparisonBlock, DestinationsBlock, JourneysBlock, PlacesBlock, RouteBlock, TrainsBlock } from '../../domain/blocks';
import { mapsRouteLink, mapsSearchLink } from '../../domain/blocks';
import { Photo, slotForPlaces } from '../../lib/imagery';
import { cx } from '../ui';

const GeoMap = lazy(() => import('./GeoMap'));
const TINTS = ['from-turquoise-50 to-ocean-100', 'from-coral-50 to-coral-100', 'from-sun-50 to-gold-soft', 'from-forest-50 to-forest-100'];

type Ask = (text: string) => void;

export function Blocks({ blocks, onAsk }: { blocks: Block[]; onAsk?: Ask }) {
  if (!blocks.length) return null;
  return (
    <div className="mt-3 space-y-3">
      {blocks.map((b, i) => (
        <section key={i} aria-label={('title' in b && b.title) || labelOf(b)}>
          {b.type === 'destinations' && <Destinations b={b} onAsk={onAsk} />}
          {b.type === 'comparison' && <Comparison b={b} onAsk={onAsk} />}
          {b.type === 'places' && <Places b={b} onAsk={onAsk} />}
          {b.type === 'route' && <Route b={b} />}
          {b.type === 'trains' && <Trains b={b} />}
          {b.type === 'journeys' && <Journeys b={b} />}
        </section>
      ))}
    </div>
  );
}
const labelOf = (b: Block) => (b.type === 'destinations' ? 'Destination ideas' : b.type === 'comparison' ? 'Comparison' : b.type === 'places' ? 'Places' : b.type === 'trains' ? 'Live trains' : b.type === 'journeys' ? 'Journey options' : 'Route');

function Title({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 px-1 text-sm font-semibold text-muted">{children}</h3>;
}

function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded-full bg-ivory px-2.5 py-0.5 text-xs text-ink">{children}</span>;
}

function Fact({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return <li className="flex items-start gap-2 text-sm"><span className="mt-0.5 shrink-0 text-forest-700">{icon}</span><span className="min-w-0">{children}</span></li>;
}

function AskButton({ onAsk, text, children, primary }: { onAsk?: Ask; text: string; children: ReactNode; primary?: boolean }) {
  if (!onAsk) return null;
  return (
    <button type="button" onClick={() => onAsk(text)}
      className={cx('inline-flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-sm font-semibold transition', primary ? 'bg-forest-700 text-white hover:bg-forest-800' : 'text-forest-700 hover:bg-forest-50')}>
      {children}
    </button>
  );
}

function MapLink({ href, children }: { href: string; children: ReactNode }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-sm font-semibold text-forest-700 hover:bg-forest-50"><Navigation className="size-4" aria-hidden />{children}</a>;
}

// ---------------------------------------------------------------- destinations

function Destinations({ b, onAsk }: { b: DestinationsBlock; onAsk?: Ask }) {
  return (
    <>
      {b.title && <Title>{b.title}</Title>}
      <ul className="scroll-row -mx-1 px-1 sm:grid sm:grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] sm:overflow-visible">
        {b.items.map((d, i) => {
          const slot = slotForPlaces(d.name, d.country);
          const hasPhoto = slot !== 'map' && slot !== 'coast' ? true : /coast|beach|island|alps|mountain/i.test(`${d.name} ${d.summary}`);
          return (
            <li key={d.name} className="flex w-[17rem] shrink-0 flex-col overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-card)] sm:w-auto">
              <div className={cx('relative h-32 overflow-hidden bg-gradient-to-br', TINTS[i % TINTS.length])}>
                {hasPhoto ? <Photo slot={slot} width={600} className="absolute inset-0" />
                  : d.lat != null && d.lon != null ? (
                    <Suspense fallback={null}><div className="absolute inset-0"><GeoMap points={[{ lat: d.lat, lon: d.lon }]} numbered={false} zoomOut={7} height={128} label={`Where ${d.name} is`} /></div></Suspense>
                  ) : <span className="absolute inset-0 flex items-center justify-center font-editorial text-6xl text-ink/15" aria-hidden>{d.name.charAt(0)}</span>}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h4 className="font-editorial text-2xl leading-tight">{d.name}</h4>
                {d.country && <p className="text-sm text-muted">{d.country}</p>}
                <p className="mt-2 text-sm text-ink/90">{d.summary}</p>
                {d.highlights.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{d.highlights.map((h) => <Chip key={h}>{h}</Chip>)}</div>}
                {(d.best_time || d.budget || d.getting_there) && (
                  <ul className="mt-3 space-y-1">
                    {d.getting_there && <Fact icon={<TrainFront className="size-4" aria-hidden />}>{d.getting_there}</Fact>}
                    {d.best_time && <Fact icon={<CalendarDays className="size-4" aria-hidden />}>{d.best_time}</Fact>}
                    {d.budget && <Fact icon={<Wallet className="size-4" aria-hidden />}>{d.budget}</Fact>}
                  </ul>
                )}
                <div className="mt-auto flex flex-wrap gap-1 pt-3">
                  <AskButton onAsk={onAsk} primary text={`Let's plan a trip to ${d.name}${d.country ? `, ${d.country}` : ''}.`}><Sparkles className="size-4" aria-hidden />Plan this</AskButton>
                  <AskButton onAsk={onAsk} text={`Tell me more about ${d.name}.`}>More</AskButton>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

// ---------------------------------------------------------------- comparison

function Comparison({ b, onAsk }: { b: ComparisonBlock; onAsk?: Ask }) {
  return (
    <div className="rounded-3xl bg-white p-3 shadow-[var(--shadow-card)] sm:p-4">
      <Title>{b.title}</Title>
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,13rem),1fr))] gap-3">
        {b.options.map((o) => (
          <li key={o.name} className={cx('flex flex-col rounded-2xl border p-3.5', o.recommended ? 'border-forest-500 bg-forest-50/50' : 'border-line')}>
            {o.recommended && <span className="mb-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-forest-700 px-2.5 py-0.5 text-xs font-semibold text-white"><Star className="size-3" aria-hidden />Elio’s pick</span>}
            <h4 className="text-base font-semibold leading-snug">{o.name}</h4>
            {o.subtitle && <p className="text-sm text-muted">{o.subtitle}</p>}
            {o.price && <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{o.price}</p>}
            {o.pros.length > 0 && <ul className="mt-2 space-y-1">{o.pros.map((p) => <li key={p} className="flex gap-2 text-sm"><Check className="mt-0.5 size-4 shrink-0 text-ok-700" aria-label="Pro" />{p}</li>)}</ul>}
            {o.cons.length > 0 && <ul className="mt-1.5 space-y-1">{o.cons.map((c) => <li key={c} className="flex gap-2 text-sm text-ink/80"><Minus className="mt-0.5 size-4 shrink-0 text-coral-700" aria-label="Con" />{c}</li>)}</ul>}
            <div className="mt-auto flex flex-wrap gap-1 pt-3">
              <AskButton onAsk={onAsk} primary={o.recommended} text={`Let's go with ${o.name}.`}>Choose<ArrowRight className="size-4" aria-hidden /></AskButton>
              {o.url && <a href={o.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex min-h-9 items-center gap-1 whitespace-nowrap rounded-full px-3 text-sm font-semibold text-forest-700 hover:bg-forest-50"><ExternalLink className="size-4" aria-hidden />Website</a>}
            </div>
          </li>
        ))}
      </ul>
      {b.recommendation && <p className="mt-3 flex gap-2 rounded-2xl bg-ivory p-3 text-sm"><Sparkles className="mt-0.5 size-4 shrink-0 text-forest-700" aria-hidden />{b.recommendation}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- places

function Places({ b, onAsk }: { b: PlacesBlock; onAsk?: Ask }) {
  const google = b.source === 'google';
  // Google's terms: its places are not drawn on other maps, and results carry a "Google Maps" credit.
  const located = google ? [] : b.items.filter((p) => p.lat != null && p.lon != null);
  return (
    <div className="overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-card)]">
      {located.length >= 2 && located.length === b.items.length && (
        <Suspense fallback={<div className="h-40 bg-[#e3f4f3]" />}>
          <GeoMap points={b.items.map((p) => ({ lat: p.lat!, lon: p.lon! }))} height={180} label={`Map of ${b.items.length} places. The list below has the same places.`} />
        </Suspense>
      )}
      <div className="p-3 sm:p-4">
        {b.title && <Title>{b.title}</Title>}
        <ol className="divide-y divide-line">
          {b.items.map((p, i) => (
            <li key={p.name} className="flex gap-3 py-3 first:pt-1 last:pb-1">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-forest-700 text-xs font-bold text-white">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <h4 className="font-semibold leading-snug">{p.name}</h4>
                <p className="text-xs text-muted">{[p.kind, p.area, p.price].filter(Boolean).join(' · ')}</p>
                {(p.rating != null || p.open_now != null) && (
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
                    {p.rating != null && <span className="inline-flex items-center gap-0.5 whitespace-nowrap font-semibold"><Star className="size-3.5 fill-sun-500 text-sun-500" aria-hidden />{p.rating.toFixed(1)}{p.rating_count != null && <span className="font-normal text-muted"> ({p.rating_count.toLocaleString('en-GB')})</span>}</span>}
                    {p.open_now != null && <span className={cx('whitespace-nowrap font-semibold', p.open_now ? 'text-ok-700' : 'text-coral-700')}>{p.open_now ? 'Open now' : 'Closed now'}</span>}
                  </p>
                )}
                <p className="mt-1 text-sm text-ink/90">{p.why}</p>
                {p.hours && <p className="mt-1 flex items-center gap-1.5 text-xs text-muted"><Clock className="size-3.5" aria-hidden />{p.hours}</p>}
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <MapLink href={p.maps_url ?? (p.place_id ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.name)}&query_place_id=${encodeURIComponent(p.place_id)}` : mapsSearchLink(p.name, p.area))}>Map</MapLink>
                  {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex min-h-9 items-center gap-1 whitespace-nowrap rounded-full px-3 text-sm font-semibold text-forest-700 hover:bg-forest-50"><ExternalLink className="size-4" aria-hidden />Website</a>}
                  <AskButton onAsk={onAsk} text={`Add ${p.name}${p.area ? ` (${p.area})` : ''} to my plan.`}><MapPin className="size-4" aria-hidden />Add to plan</AskButton>
                </div>
              </div>
            </li>
          ))}
        </ol>
        {google && <p className="mt-2 text-right text-xs text-[#5e5e5e]" translate="no" style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>Google Maps</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- live trains

function Trains({ b }: { b: TrainsBlock }) {
  const at = new Date(b.fetched_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' });
  const conn = b.kind === 'connections';
  return (
    <div className="overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-card)]">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-ink px-4 py-3 text-white">
        <p className="flex items-center gap-2 font-semibold"><TrainFront className="size-4" aria-hidden />{conn ? <>{b.station} <ArrowRight className="size-4" aria-hidden /> {b.to}</> : <>Departures · {b.station}</>}</p>
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-white/80"><span className="size-2 animate-pulse rounded-full bg-ok-100" aria-hidden />Live at {at}</span>
      </div>
      {b.rows.length === 0 ? <p className="p-4 text-sm text-muted">No trains found for that time.</p> : (
        <ul className="divide-y divide-line">
          {b.rows.map((t, i) => (
            <li key={i} className={cx('flex items-center gap-3 px-4 py-2.5', t.canceled && 'bg-danger-soft/40')}>
              <div className="w-16 shrink-0 tabular-nums">
                <p className={cx('font-semibold', (t.delay_min > 0 || t.canceled) && 'text-muted line-through decoration-1')}>{t.time}</p>
                {t.delay_min > 0 && !t.canceled && <p className="text-xs font-semibold text-coral-700">+{t.delay_min} min</p>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium leading-snug">{conn ? t.train : t.to}</p>
                <p className="text-xs text-muted">{conn ? [t.changes ? `${t.changes} change${t.changes > 1 ? 's' : ''}` : 'Direct', `arrives ${t.arrival_time ?? '?'}${t.arrival_delay_min ? ` (+${t.arrival_delay_min})` : ''}`, t.duration_min ? `${Math.floor(t.duration_min / 60) ? `${Math.floor(t.duration_min / 60)} h ` : ''}${t.duration_min % 60} min` : null].filter(Boolean).map((x, i) => <span key={i}>{i > 0 && ' · '}<span className="whitespace-nowrap">{x}</span></span>) : t.train}</p>
              </div>
              <div className="shrink-0 text-right">
                {t.canceled ? <span className="rounded-full bg-danger-soft px-2 py-0.5 text-xs font-semibold text-danger">Cancelled</span> : t.platform && (
                  <span className={cx('inline-flex flex-col items-center rounded-xl px-2.5 py-1 text-xs', t.platform_changed ? 'bg-warn-soft text-warn' : 'bg-ivory text-ink')}>
                    <span className="text-[0.625rem] uppercase tracking-wide opacity-70">{t.platform_changed ? 'Changed' : 'Platform'}</span><span className="text-base font-bold leading-tight">{t.platform}</span>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="border-t border-line px-4 py-2 text-xs text-muted">{b.source === 'irail' ? 'Live SNCB/NMBS data via iRail.' : 'Live NS data.'} Platforms can still change: check the station boards.</p>
    </div>
  );
}

// ---------------------------------------------------------------- journeys (Google, worldwide)

const LEG_ICON: Record<string, ReactNode> = {
  train: <TrainFront className="size-3.5" aria-hidden />, metro: <TrainFront className="size-3.5" aria-hidden />, tram: <TramFront className="size-3.5" aria-hidden />,
  bus: <Bus className="size-3.5" aria-hidden />, ferry: <Ship className="size-3.5" aria-hidden />, 'cable car': <CableCar className="size-3.5" aria-hidden />,
  walk: <Footprints className="size-3.5" aria-hidden />,
};
const dur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`);

function Journeys({ b }: { b: JourneysBlock }) {
  return (
    <div className="overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-card)]">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-line px-4 py-3">
        <Navigation className="size-4 text-forest-700" aria-hidden />
        <p className="min-w-0 flex-1 font-semibold leading-snug">{b.from} <ArrowRight className="inline size-4 align-[-3px] text-muted" aria-hidden /> {b.to}</p>
        <a href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(b.from)}&destination=${encodeURIComponent(b.to)}&travelmode=transit`} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap text-sm font-semibold text-forest-700 hover:underline">Open in Maps</a>
      </div>
      {!b.live || b.options.length === 0 ? <p className="p-4 text-sm text-muted">Journey times are shown live and not saved. Ask Elio again for current options.</p> : (
        <ol className="divide-y divide-line">
          {b.options.map((o, i) => (
            <li key={i} className="px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className="text-lg font-semibold tabular-nums">{o.depart && o.arrive ? <>{o.depart} <span className="text-muted">→</span> {o.arrive}</> : dur(o.minutes)}</p>
                <p className="whitespace-nowrap text-sm text-muted">{dur(o.minutes)} · {o.changes ? `${o.changes} change${o.changes > 1 ? 's' : ''}` : 'direct'}{o.fare ? ` · ${o.fare}` : ''}</p>
              </div>
              <ul className="mt-2 flex flex-wrap items-center gap-1.5">
                {o.legs.map((l, j) => (
                  <li key={j} className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs', l.mode === 'walk' ? 'text-muted' : 'bg-forest-50 font-semibold text-forest-800')}>
                    {LEG_ICON[l.mode] ?? <TrainFront className="size-3.5" aria-hidden />}{l.mode === 'walk' ? `${l.minutes} min` : l.line ?? l.vehicle ?? l.mode}
                  </li>
                ))}
              </ul>
              <ol className="mt-2 space-y-1">
                {o.legs.filter((l) => l.mode !== 'walk').map((l, j) => (
                  <li key={j} className="text-xs text-ink/80">
                    <span className="font-semibold tabular-nums">{l.depart}</span> {l.from} → <span className="font-semibold tabular-nums">{l.arrive}</span> {l.to}
                    <span className="text-muted">{[l.headsign && ` · towards ${l.headsign}`, l.agency && ` · ${l.agency}`].filter(Boolean).join('')}</span>
                  </li>
                ))}
              </ol>
            </li>
          ))}
        </ol>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2">
        <p className="text-xs text-muted">Timetabled times; platforms not shown. Check with the operator.</p>
        <p className="text-xs text-[#5e5e5e]" translate="no" style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>Google Maps</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- route

const MODE: Record<string, { icon: ReactNode; label: string }> = {
  train: { icon: <TrainFront className="size-4" aria-hidden />, label: 'Train' },
  car: { icon: <Car className="size-4" aria-hidden />, label: 'Drive' },
  bus: { icon: <Bus className="size-4" aria-hidden />, label: 'Bus' },
  flight: { icon: <Plane className="size-4" aria-hidden />, label: 'Flight' },
  ferry: { icon: <Ship className="size-4" aria-hidden />, label: 'Ferry' },
  walk: { icon: <Footprints className="size-4" aria-hidden />, label: 'Walk' },
  bike: { icon: <Bike className="size-4" aria-hidden />, label: 'Bike' },
};

function Route({ b }: { b: RouteBlock }) {
  const mainMode = b.legs[0]?.mode ?? 'train';
  return (
    <div className="overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-card)]">
      <Suspense fallback={<div className="h-52 bg-[#e3f4f3]" />}>
        <GeoMap points={b.stops.map((s) => ({ lat: s.lat, lon: s.lon, label: s.name }))} legs={b.legs} connect height={240}
          label={`Route map: ${b.stops.map((s) => s.name).join(', then ')}. The list below has the same route.`} />
      </Suspense>
      <div className="p-3 sm:p-4">
        {b.title && <Title>{b.title}</Title>}
        <ol>
          {b.stops.map((s, i) => {
            const leg = b.legs[i];
            return (
              <li key={`${s.name}-${i}`}>
                <div className="flex gap-3">
                  <span className={cx('flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white', i === 0 ? 'bg-coral-500' : 'bg-forest-700')}>{i + 1}</span>
                  <div className="min-w-0 flex-1 pb-1">
                    <p className="font-semibold leading-snug">{s.name}{s.dates && <span className="ml-2 whitespace-nowrap text-sm font-normal text-muted">{s.dates}</span>}</p>
                    {s.note && <p className="text-sm text-ink/80">{s.note}</p>}
                  </div>
                </div>
                {i < b.stops.length - 1 && (
                  <div className="ml-3.5 flex items-center gap-2 border-l-2 border-dashed border-forest-200 py-2 pl-5 text-xs text-muted">
                    {MODE[leg?.mode ?? 'train']!.icon}<span className="whitespace-nowrap">{MODE[leg?.mode ?? 'train']!.label}{leg?.duration ? ` · ${leg.duration}` : ''}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted">Map is a sketch. Durations are typical, not live.</p>
          <MapLink href={mapsRouteLink(b.stops, mainMode)}>Open in Google Maps</MapLink>
        </div>
      </div>
    </div>
  );
}
